import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface MobileAnalytics {
  readonly range: { readonly fromDate: string | null; readonly toDate: string | null };
  readonly deviceAdoptionByPlatform: Record<string, number>;
  readonly deviceByStatus: Record<string, number>;
  readonly syncByStatus: Record<string, number>;
  readonly syncSuccessRate: number;
  readonly media: { readonly photos: number; readonly videos: number; readonly documents: number };
  readonly gpsPoints: number;
  readonly gpsDistinctUsers: number;
  readonly securityBySeverity: Record<string, number>;
}

function CountMap({ label, title, map }: { label: string; title: string; map: Record<string, number> }): ReactNode {
  const { t } = useTranslation();
  const entries = Object.entries(map);
  return (
    <section aria-label={label}>
      <h2>{title}</h2>
      {entries.length === 0 ? (
        <p>{t('m22.telemetry.noData')}</p>
      ) : (
        <ul>
          {entries.map(([k, v]) => (
            <li key={k}>{k}: {v}</li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * M22 L22.03 — Mobile Telemetry Dashboard.
 * Renders device adoption, sync reliability, media volume, GPS coverage and
 * security posture from the M22 analytics API (/v1/mobile/analytics).
 */
export function M22TelemetryDashboardPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [data, setData] = useState<MobileAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: MobileAnalytics }>('/v1/mobile/analytics', { token })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  if (error) return <p role="alert">{error}</p>;
  if (!data) return <p>{t('m22.telemetry.loading')}</p>;
  return (
    <main aria-label="mobile-telemetry">
      <h1>{t('m22.telemetry.title')}</h1>
      <p aria-label="sync-success-rate">{t('m22.telemetry.syncSuccessRate', { rate: data.syncSuccessRate })}</p>
      <p aria-label="gps-coverage">{t('m22.telemetry.gpsCoverage', { points: data.gpsPoints, users: data.gpsDistinctUsers })}</p>
      <p aria-label="media-volume">
        {t('m22.telemetry.mediaVolume', { photos: data.media.photos, videos: data.media.videos, documents: data.media.documents })}
      </p>
      <CountMap label="Device Adoption by Platform" title={t('m22.telemetry.deviceAdoption')} map={data.deviceAdoptionByPlatform} />
      <CountMap label="Devices by Status" title={t('m22.telemetry.devicesByStatus')} map={data.deviceByStatus} />
      <CountMap label="Sync by Status" title={t('m22.telemetry.syncByStatus')} map={data.syncByStatus} />
      <CountMap label="Security Events by Severity" title={t('m22.telemetry.securityBySeverity')} map={data.securityBySeverity} />
    </main>
  );
}
